import { cn } from '@sohanemon/utils';
import { useMediaQuery } from '@sohanemon/utils/hooks';
import { m } from '@sohanscript/i18n';
import { useScrollTransform } from '@sohanscript/lib/hooks/scroll-transform';
import OrbitingCircles from '@sohanscript/ui/animated/orbiting-circle';
import { Heading } from '@sohanscript/ui/heading';
import { Motion } from '@sohanscript/ui/motion';
import { StackCircle } from '@sohanscript/ui/stack-circle';
import { domAnimation, LazyMotion } from 'motion/react';
import { type ComponentProps, useRef } from 'react';
import { Img } from '@/components/image';
import { api } from '@/lib/trpc/client';
import type { RouterOutputs } from '@/server/root';

type StackItem = RouterOutputs['content']['stack']['stack'][number];
type StackProps = ComponentProps<'section'>;

function renderStackCircle(item: StackItem | undefined) {
	if (!item?.image || !item?.label) return null;
	return (
		<StackCircle
			image={item.image}
			label={item.label}
			renderImage={({ src, className }) => (
				<Img placeholder="none" width={50} src={src} className={className} />
			)}
		/>
	);
}

export function Stack({ className, ...props }: StackProps) {
	const target = useRef(null);
	const lg = useMediaQuery('lg');
	const transform = useScrollTransform({
		enabled: lg,
		target,
		input: [0, 0.5, 1],
		output: { rotate: [360, 0, -360], scale: lg ? [0.5, 1, 0.5] : undefined },
		offset: ['start end', 'end start'],
		spring: true,
		forceAnimate: true,
	});

	const [data] = api.content.stack.useSuspenseQuery();

	const stackItems = data?.stack ?? [];

	return (
		<LazyMotion features={domAnimation}>
			<section
				ref={target}
				aria-labelledby="stack-heading"
				className={cn('container', className)}
				{...props}
			>
				<div className="flex w-full grid-cols-2 flex-col items-center justify-end md:grid">
					<Heading
						variant="start"
						titlePrefix={m.heading_stack_prefix()}
						titleHighlight={m.heading_stack_highlight()}
						description={m.stack_heading_tagline()}
						className="w-fit"
					/>
					<Motion
						as="div"
						className="relative z-10 flex flex-1 flex-col items-center justify-center gap-y-6 max-md:rotate-0!"
						// @ts-expect-error undefined is fine;
						style={transform.output}
					>
						<div className="relative flex h-[500px] w-screen flex-col items-center justify-center overflow-hidden lg:w-full">
							{renderStackCircle(stackItems[0])}
							{Array.from({ length: 2 }, (_, idx) => (
								<OrbitingCircles key={idx} index={idx} total={2} radius={120}>
									{renderStackCircle(stackItems[idx + 1])}
								</OrbitingCircles>
							))}
							{Array.from({ length: 3 }, (_, index) => (
								<OrbitingCircles
									key={index + 2}
									index={index}
									reverse
									total={3}
									radius={190}
								>
									{renderStackCircle(stackItems[index + 3])}
								</OrbitingCircles>
							))}
						</div>
					</Motion>
				</div>
			</section>
		</LazyMotion>
	);
}
